import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn } from 'formik';
import { Grid, Collapse, IconButton } from '@material-ui/core';
import caret from 'core/assets/svg/caretOrange.svg';
import {
  FarmRiskType,
  FarmScheduleCoverageType,
  FarmScheduleCoverageTypesByState,
  lookupsJson,
  ScheduledFarmItemTypes
} from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { ActionButton } from 'core/components/action-button';
import Field from 'core/components/form/form.v2';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import Label from 'core/components/label/label';
import { getWindHailOptions } from './wind-hail-deductible-options';
import { MACHINERY_CATEGORIES, OPEN_PERILS_CATEGORIES, RCC_CATEGORIES } from './scheduled-farm-property-constants';

import useStyles from './scheduled-farm-property-item.styles';

const { scheduledFarmPropertyCategories, increaseStackLimitOptions, deductibleAllOther } = lookupsJson.farm;

const riskType = FarmRiskType.Fo6Scheduled;

const ScheduledFarmPropertyItem = ({ item, index, onDelete, fieldName, coverageType, disabled }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const { values: formValues, setFieldValue } = useFormikContext();

  const availableItemTypes = scheduledFarmPropertyCategories;

  // Find the FO6Scheduled risk index
  const locationRisks = getIn(formValues, `${fieldName}.risks`);
  const fo6ScheduledRiskIndex = locationRisks.findIndex((risk) => risk.type === 'FO6Scheduled');
  const itemFieldPath = `${fieldName}.risks[${fo6ScheduledRiskIndex}].coverages.${coverageType}[${index}]`;

  const currentItem = getIn(formValues, itemFieldPath) || item;

  const address = getIn(formValues, `${fieldName}.address`);
  const covFConfig = appConfig.farm[address?.state]?.risks[riskType]?.coverageF;
  const availableScheduledCoverageTypes =
    appConfig.farm[address?.state]?.risks[riskType]?.allowedOptions?.scheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[address?.state] ||
    [];
  const overrides = covFConfig?.itemOverrides?.[currentItem.category];

  const deductibleOptions =
    currentItem.category === ScheduledFarmItemTypes.LIVESTOCK && covFConfig?.livestockDeductibleOptions
      ? covFConfig.livestockDeductibleOptions
      : covFConfig?.deductibleOptions || deductibleAllOther;

  const showMachineryFields = MACHINERY_CATEGORIES.includes(currentItem.category) && !overrides?.hideMakeField;
  const showSerialNumber = MACHINERY_CATEGORIES.includes(currentItem.category) && !overrides?.hideSerialNumber;
  const showRCC = RCC_CATEGORIES.includes(currentItem.category) && !overrides?.hideReplacementCost;
  const showOpenPerils = (() => {
    if (overrides?.hideOpenPerils) {
      return false;
    }
    if (overrides?.openPerilsAlwaysOn) {
      return true;
    }
    return OPEN_PERILS_CATEGORIES.includes(currentItem.category);
  })();

  const windHailResult = useMemo(
    () =>
      covFConfig?.showWindHailDeductible
        ? getWindHailOptions({
            coverage: currentItem.limit,
            allPerilDeductible: currentItem.deductible,
            floor: covFConfig?.windHailDeductibleFloor
          })
        : null,
    [currentItem.limit, currentItem.deductible, covFConfig?.showWindHailDeductible, covFConfig?.windHailDeductibleFloor]
  );

  // Auto-set wind/hail deductible when options change (e.g. all-peril deductible changed)
  useEffect(() => {
    if (windHailResult?.targetValue != null) {
      const validIds = new Set(windHailResult.options.map((o) => o.id));
      if (!validIds.has(currentItem.deductibleWindHail)) {
        setFieldValue(`${itemFieldPath}.deductibleWindHail`, windHailResult.targetValue);
      }
    }
  }, [windHailResult?.targetValue, windHailResult?.options, itemFieldPath]);

  // Sync always-on overrides with Formik state
  useEffect(() => {
    if (overrides?.openPerilsAlwaysOn && !currentItem.hasOpenPerils) {
      setFieldValue(`${itemFieldPath}.hasOpenPerils`, true);
    }
  }, [overrides?.openPerilsAlwaysOn]);

  useEffect(() => {
    if (overrides?.winterPerilsAlwaysOn && !currentItem.hasWinterPerils) {
      setFieldValue(`${itemFieldPath}.hasWinterPerils`, true);
    }
  }, [overrides?.winterPerilsAlwaysOn]);

  // Clear additionalPerils when the field is hidden to avoid submitting stale data
  useEffect(() => {
    if (overrides?.hideAdditionalPerils && currentItem.additionalPerils != null) {
      setFieldValue(`${itemFieldPath}.additionalPerils`, null);
    }
  }, [overrides?.hideAdditionalPerils]);

  // Clear increaseStackLimit when the field is hidden to avoid submitting stale data
  useEffect(() => {
    if (overrides?.hideIncreaseStackLimit && currentItem.increaseStackLimit != null) {
      setFieldValue(`${itemFieldPath}.increaseStackLimit`, null);
    }
  }, [overrides?.hideIncreaseStackLimit]);

  // Clear serialNumber when the field is hidden to avoid submitting stale data
  useEffect(() => {
    if (overrides?.hideSerialNumber && currentItem.serialNumber != null) {
      setFieldValue(`${itemFieldPath}.serialNumber`, null);
    }
  }, [overrides?.hideSerialNumber]);

  const getItemTypeName = (categoryId) => {
    const itemType = availableItemTypes.find((type) => type.id === categoryId);
    return itemType?.value || 'Unknown';
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(index);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={classes.item}>
      <div className={classes.itemTitleRow}>
        <Grid xs={10}>
          <Label className={classes.itemName}>
            {currentItem.extendedDetails?.itemName || currentItem.description || 'Item Name'}
          </Label>
          <div className={classes.itemType}>{getItemTypeName(currentItem.category)}</div>
        </Grid>
        <div className={classes.divider} />
        {currentItem.deductible && (
          <>
            <Grid className={classes.itemStack} xs={3}>
              <Label className={classes.itemLabel}>Deductible</Label>
              <Label className={classes.itemValue}>${currentItem.deductible?.toLocaleString()}</Label>
            </Grid>
            <div className={classes.divider} />
          </>
        )}
        <Grid className={classes.itemStack} xs={3}>
          <Label className={classes.itemLabel}>Coverage</Label>
          <Label className={classes.itemValue}>${currentItem.limit?.toLocaleString()}</Label>
        </Grid>
        <Grid xs={3} container justifyContent="flex-end">
          <ActionButton
            onClick={handleDelete}
            className={classes.deleteButton}
            icon={deleteIcon}
            size="small"
            disabled={disabled}
          />
          <IconButton onClick={toggleExpanded} className={classes.expandIcon} size="small">
            <img
              className={isExpanded ? classes.icon : classes.iconFold}
              src={caret}
              alt={isExpanded ? 'fold' : 'unfold'}
            />
          </IconButton>
        </Grid>
      </div>

      <Collapse in={isExpanded}>
        <div className={classes.formContainer}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Field
              xs={6}
              type="select"
              id={`${itemFieldPath}.category`}
              name={`${itemFieldPath}.category`}
              mode="light"
              label="Type of item"
              options={availableItemTypes}
              disabled={true}
            />
            <Field
              xs={6}
              type="string"
              id={`${itemFieldPath}.description`}
              name={`${itemFieldPath}.description`}
              mode="light"
              label={overrides?.descriptionLabel || 'Item name'}
              disabled={disabled}
            />
            <Field
              xs={6}
              type="select"
              id={`${itemFieldPath}.deductible`}
              name={`${itemFieldPath}.deductible`}
              mode="light"
              label="Deductible"
              options={deductibleOptions}
              disabled={disabled}
            />
            <Field
              xs={6}
              type="numeric"
              id={`${itemFieldPath}.limit`}
              name={`${itemFieldPath}.limit`}
              mode="light"
              label="Coverage amount"
              format={{ thousandSeparator: true, prefix: '$' }}
              disabled={disabled}
            />

            {overrides?.showYear && (
              <Field
                xs={6}
                type="numeric"
                id={`${itemFieldPath}.year`}
                name={`${itemFieldPath}.year`}
                mode="light"
                label="Year"
                disabled={disabled}
              />
            )}

            {covFConfig?.showWindHailDeductible && windHailResult && (
              <Field
                xs={6}
                type="select"
                id={`${itemFieldPath}.deductibleWindHail`}
                name={`${itemFieldPath}.deductibleWindHail`}
                mode="light"
                label="Wind/hail deductible"
                options={windHailResult.options}
                disabled={disabled || windHailResult.isOverride}
              />
            )}

            {showSerialNumber && (
              <Field
                xs={6}
                type="string"
                id={`${itemFieldPath}.serialNumber`}
                name={`${itemFieldPath}.serialNumber`}
                mode="light"
                label="Serial number"
                disabled={disabled}
              />
            )}

            {showMachineryFields && (
              <Field
                xs={6}
                type="string"
                id={`${itemFieldPath}.make`}
                name={`${itemFieldPath}.make`}
                mode="light"
                label="Make/model"
                disabled={disabled}
              />
            )}

            {showRCC && (
              <Field
                xs={6}
                type="checkbox"
                id={`${itemFieldPath}.hasReplacementCostCoverage`}
                name={`${itemFieldPath}.hasReplacementCostCoverage`}
                mode="light"
                label="Replacement cost coverage"
                disabled={disabled}
              />
            )}

            {showOpenPerils && (
              <Field
                xs={6}
                type="checkbox"
                id={`${itemFieldPath}.hasOpenPerils`}
                name={`${itemFieldPath}.hasOpenPerils`}
                mode="light"
                label={overrides?.openPerilsAlwaysOn ? 'Includes intake of foreign objects' : 'Open perils'}
                subLabel={overrides?.openPerilsAlwaysOn ? undefined : 'Including intake of foreign objects'}
                disabled={disabled || overrides?.openPerilsAlwaysOn}
              />
            )}

            {/* No useEffect cleanup needed for quantity — the category field is disabled (immutable once set) */}
            {currentItem.category === ScheduledFarmItemTypes.HAY && (
              <Field
                xs={6}
                type="numeric"
                id={`${itemFieldPath}.quantity`}
                name={`${itemFieldPath}.quantity`}
                mode="light"
                label="Quantity (informational only)"
                disabled={disabled}
              />
            )}

            {currentItem.category === ScheduledFarmItemTypes.HAY && !overrides?.hideIncreaseStackLimit && (
              <Field
                xs={12}
                type="select"
                id={`${itemFieldPath}.increaseStackLimit`}
                name={`${itemFieldPath}.increaseStackLimit`}
                mode="light"
                label="Increase stack limit"
                options={increaseStackLimitOptions}
                disabled={disabled}
              />
            )}

            {currentItem.category === ScheduledFarmItemTypes.LIVESTOCK && (
              <>
                <Field
                  xs={6}
                  type="numeric"
                  id={`${itemFieldPath}.quantity`}
                  name={`${itemFieldPath}.quantity`}
                  mode="light"
                  label="Quantity (informational only)"
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="select"
                  id={`${itemFieldPath}.livestockCategory`}
                  name={`${itemFieldPath}.livestockCategory`}
                  mode="light"
                  label="Livestock type"
                  options={lookupsJson.farm.livestockTypes}
                  disabled={disabled}
                  onChange={(val) => {
                    if (val !== 'cattle') {
                      setFieldValue(`${itemFieldPath}.hasSpecialCattleEndorsement`, false);
                    }
                  }}
                />
                {!overrides?.hideAdditionalPerils && (
                  <Field
                    xs={6}
                    type="select"
                    id={`${itemFieldPath}.additionalPerils`}
                    name={`${itemFieldPath}.additionalPerils`}
                    mode="light"
                    label="Additional Perils"
                    options={lookupsJson.farm.livestockAdditionalPerils}
                    disabled={disabled}
                  />
                )}
                <Field
                  xs={6}
                  type="checkbox"
                  id={`${itemFieldPath}.hasWinterPerils`}
                  name={`${itemFieldPath}.hasWinterPerils`}
                  mode="light"
                  label="Winter Perils - Livestock"
                  disabled={disabled || overrides?.winterPerilsAlwaysOn}
                />
                {currentItem.hasWinterPerils && (
                  <Field
                    xs={6}
                    type="numeric"
                    id={`${itemFieldPath}.winterPerilsLimit`}
                    name={`${itemFieldPath}.limit`}
                    mode="light"
                    label="Winter Perils - Limit"
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={true}
                  />
                )}
                {!overrides?.hideSuffocationOfLivestock && (
                  <Field
                    xs={6}
                    type="checkbox"
                    id={`${itemFieldPath}.hasSuffocationOfLivestock`}
                    name={`${itemFieldPath}.hasSuffocationOfLivestock`}
                    mode="light"
                    label="Suffocation of livestock"
                    disabled={disabled}
                  />
                )}
                {!overrides?.hideSpecialCattleEndorsement && (
                  <Field
                    xs={6}
                    type="checkbox"
                    id={`${itemFieldPath}.hasSpecialCattleEndorsement`}
                    name={`${itemFieldPath}.hasSpecialCattleEndorsement`}
                    mode="light"
                    label="Special cattle endorsement"
                    subLabel="Multiple loss coverage"
                    disabled={disabled || currentItem.livestockCategory !== 'cattle'}
                  />
                )}
              </>
            )}

            <Grid
              container
              justifyContent={currentItem.category === ScheduledFarmItemTypes.LIVESTOCK ? 'space-between' : 'flex-end'}
              className={classes.sectionFooter}
            >
              {currentItem.category === ScheduledFarmItemTypes.LIVESTOCK &&
                availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.ANIMALS_4H_FFA) && (
                  <Grid className={classes.livestockInfoTextContainer} xs={10}>
                    <InfoIcon width={16} height={16} />
                    <span className={classes.livestockInfoText}>
                      This is for livestock only. If you'd like to add animals (Under 4-H & FFA programs), please go to
                      that section.
                    </span>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </div>
      </Collapse>
    </div>
  );
};

ScheduledFarmPropertyItem.propTypes = {
  item: PropTypes.shape({
    category: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deductible: PropTypes.number,
    deductibleWindHail: PropTypes.number,
    limit: PropTypes.number,
    description: PropTypes.string,
    serialNumber: PropTypes.string,
    make: PropTypes.string,
    year: PropTypes.number,
    hasReplacementCostCoverage: PropTypes.bool,
    hasOpenPerils: PropTypes.bool,
    increaseStackLimit: PropTypes.number,
    quantity: PropTypes.number,
    livestockCategory: PropTypes.string,
    includeWinterPeril: PropTypes.string,
    hasWinterPerils: PropTypes.bool,
    hasSuffocationOfLivestock: PropTypes.bool,
    hasSpecialCattleEndorsement: PropTypes.bool,
    extendedDetails: PropTypes.shape({
      itemName: PropTypes.string
    })
  }).isRequired,
  index: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
  coverageType: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

ScheduledFarmPropertyItem.defaultProps = {
  disabled: false
};

export default ScheduledFarmPropertyItem;
